import type { MentionInputType, UpdateActivityInputType, UserOption } from '../constants'
import { useActivityContext } from '../Context'
import React, { useEffect, useRef, useState } from 'react'
import { useError, useMutation } from '@ztechapps/common.hook'
import { ShowIf } from '@ztechapps/webui.util'
import isEmpty from 'lodash/isEmpty'
import { ButtonGroup, Stack } from 'react-bootstrap'
import { RichTextInput } from '@ztechapps/webui.input'
import { BusyButton } from '@ztechapps/webui.button'
import { useMention } from './useMention'
import { mutationUpdateRemarksActivityMention } from '../graphql'
import { Form } from '@ztechapps/webui.form'

export const UpdateMentionInput = ({ activity, afterSave }: MentionInputType & Partial<UpdateActivityInputType>) => {
  const [isLoading, setIsLoading] = useState(false)
  const { documentId, entityName, disabled, reset, mention } = useActivityContext()
  const { trigger, getOptions, templateKey, url, inputProps } = mention
  const chatInputRef = useRef(null)
  // Create a ref to hold the latest state value
  const activityRef = useRef('')
  const updateMutation = useMutation({ query: mutationUpdateRemarksActivityMention(entityName) })

  // This mutable object persists immediately without waiting for a re-render.
  const optionsRef = useRef<UserOption[]>([])

  const [newActivity, setNewActivity] = React.useState('')
  const [onUpdate, , { loading, error }] = updateMutation ?? []
  const activityId = activity?._id
  const initialActivity = activity?.activity ?? ''

  useError(error)

  useEffect(() => {
    if (initialActivity) {
      setNewActivity(initialActivity)
    }
  }, [initialActivity])

  const handleSubmit = async () => {
    setIsLoading(true)
    //if there is a value for activityId, then update the activity
    // Use the ref's current value in the submission logic
    const currentActivity = activityRef.current
    onUpdate({
      input: { activity: currentActivity, templateKey, url, parentId: documentId, remarksActivityId: activityId },
    })

    handleReset()
    setIsLoading(false)
  }

  useError(error)

  const handleReset = () => {
    reset()
    setNewActivity('')
    afterSave()
  }

  const { setupAutocompleter, placeholder } = useMention({ trigger, optionsRef, getOptions, handleReset, handleSubmit })

  return (
    <ShowIf condition={!disabled && !isEmpty(newActivity)}>
      <div style={{ position: 'relative' }}>
        <Form onSubmit={handleSubmit} onReset={handleReset} initialValues={{}}>
          <Stack gap={2}>
            <RichTextInput
              disabled={isLoading}
              editorRef={chatInputRef}
              value={newActivity}
              onChange={(e) => {
                setNewActivity(e)
                activityRef.current = e
              }}
              init={{
                setup: setupAutocompleter,
                menubar: false,
                toolbar: false,
                branding: false,
                height: '100px',
                placeholder,
                ...inputProps,
              }}
            />
            <div>
              <ButtonGroup size="sm">
                <BusyButton
                  type="submit"
                  disabled={!newActivity}
                  isBusy={loading}
                  text="Save"
                  icon="save"
                  busyText="Saving..."
                  title="Update activity"
                />
                <BusyButton type="reset" text="Cancel" icon="ban" title="Cancel changes" bsStyle="secondary" />
              </ButtonGroup>
            </div>
          </Stack>
        </Form>
      </div>
    </ShowIf>
  )
}
